import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { Group } from "@domain/group/group";
import { Workspace } from "@domain/group/workspace/workspace";
import { useAsync } from "@utils/hooks";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
// import { PrimaryBold } from "../../../components/containers/primary";
import { ColorSelector } from "@ui/components/inputs/colorSelector";
import { useForm } from "@ui/components/inputs/hooks";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { stack } from "@ui/components/layout";
import { ClosableModal } from "@ui/modals/closableModal";
import { workspaceColors } from "@ui/styles/colors";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";

const MAX_DESC_LENGTH = 150;
const MAX_NAME_LENGTH = 20;
interface WorkspaceEditorProps extends React.HTMLAttributes<HTMLDivElement> {
	close: () => void;
	shareroom: Group;
	initialWorkspace?: Workspace;
}
export const WorkspaceEditor: React.FC<WorkspaceEditorProps> = ({
	initialWorkspace,
	shareroom,
	close,
	...htmlProps
}) => {
	const { workspaceService } = useServices();
	const history = useHistory();

	const { fieldProps, formValue, checkSubmit } = useForm(
		{
			name: initialWorkspace?.name ?? "",
			objective: initialWorkspace?.objective ?? "",
		},
		["name", "objective"],
		{
			name: (n) => n.length < 128,
		}
	);

	const [color, setColor] = useState<string>(initialWorkspace?.color ?? workspaceColors[0]);

	const { run, loading } = useAsync(
		() =>
			workspaceService.save(
				{
					name: formValue.name,
					objective: formValue.objective,
					color,
					shareroomId: shareroom.id,
				},
				initialWorkspace?.id
			),
		[formValue.name, formValue.objective, shareroom.id, color]
	);

	return (
		<Container
			{...htmlProps}
			close={close}
			title={initialWorkspace ? I18n.t("workspace.edit.title") : I18n.t("workspace.new.title")}
		>
			<form
				onSubmit={async (e) => {
					e.preventDefault();
					try {
						if (!checkSubmit()) {
							return;
						}
						const newWorkspace = await run();
						createToast(I18n.t("success"), I18n.t("workspace.save.success"), "success");
						history.push(Path.Shareroom(shareroom, "workspaces", newWorkspace.id));
						close();
					} catch {
						createToast(I18n.t("error"), I18n.t("workspace.save.error"), "error");
					}
				}}
			>
				{/* {initialWorkspace?.isDefault ? (
					<PrimaryBold>{I18n.t("workspace.name.default")}</PrimaryBold>
				) : ( */}
				<SimpleTextInput
					textLimit={MAX_NAME_LENGTH}
					{...fieldProps("name")}
					placeholder={I18n.t("workspace.name.placeholder") + "*"}
				/>
				{/* // )} */}
				<SimpleTextInput
					textLimit={MAX_DESC_LENGTH}
					{...fieldProps("objective")}
					placeholder={I18n.t("workspace.objective.placeholder") + "*"}
				/>
				{!initialWorkspace?.isDefault && (
					<ColorSelector color={color} onColorChange={setColor} colors={workspaceColors} />
				)}
				<LoadingButton
					loading={loading}
					type="submit"
					disabled={
						!formValue.objective ||
						(formValue.objective && formValue.objective.trim().length > MAX_DESC_LENGTH) ||
						(formValue.name && formValue.name.trim().length > MAX_NAME_LENGTH) ||
						!formValue.name
					}
				>
					{I18n.t(initialWorkspace ? "validate" : "create")}
				</LoadingButton>
			</form>
		</Container>
	);
};

const Container = styled(ClosableModal)`
	${gap(40)};
	> form {
		${stack(20, "flex-start", "stretch")};
		> button {
			margin: 0 auto;
		}
	}
`;
